import { createContext, useContext } from "react";

export interface TagAliasContextType {
  /** `tag value → display name` for the whole workspace. */
  aliases: Record<string, string>;
  /** False until the first fetch settles, so a caller can tell "no names yet"
   *  from "no names at all". */
  isLoaded: boolean;
  /** Whether this session may change names — impersonation is read-only. */
  canRename: boolean;
  /** Name a value, or rename one already named. Blank clears the name. */
  rename: (value: string, displayName: string) => Promise<void>;
  /** Drop a name, so the value renders raw again. */
  clearName: (value: string) => Promise<void>;
}

// A no-alias default rather than a throwing null: the leaf components that read
// this — every chart, filter and table — are unit-tested rendered bare, and
// should degrade to showing the raw value instead of exploding. In the real app
// the provider at the root always supplies the live map.
export const TagAliasContext = createContext<TagAliasContextType>({
  aliases: {},
  isLoaded: false,
  canRename: false,
  rename: async () => {},
  clearName: async () => {},
});

/**
 * Read the workspace's tag-value display names, and change them.
 *
 * Deliberately in its own module, holding nothing but the context: the render
 * sites that consume it are leaves in charts and tables, and importing the
 * provider — which reaches auth, impersonation and the API transport — would
 * drag Vite-only `import.meta` modules into every one of their unit tests.
 */
export function useTagAliases(): TagAliasContextType {
  return useContext(TagAliasContext);
}
